import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";
import "./styles/globals.css";

// Auth Components
// import Login from "./components/auth/Login";
// import ForgotPassword from "./components/auth/ForgotPassword";
// import DashboardLayout from "../components/layout/DashboardLayout";
// import MainDashboard from "../components/dashboard/MainDashboard";
// import CampaignManagement from "../components/campaigns/CampaignManagement";
// import CampaignForm from "../components/campaigns/CampaignForm";
// import CampaignDashboard from "../components/campaigns/CampaignDashboard";
// import UserManagement from "../components/users/UserManagement";
// import TransactionHistory from "../components/transactions/TransactionHistory";
// import WalletManagement from "../components/wallets/WalletManagement";
// import FinancialOverview from "../components/financial/FinancialOverview";
// import SystemSettings from "../components/settings/SystemSettings";

// Dashboard Components
import DashboardLayout from "./components/layout/DashboardLayout";
import MainDashboard from "./components/dashboard/MainDashboard";
import CampaignManagement from "./components/campaigns/CampaignManagement";
import CampaignDashboard from "./components/campaigns/CampaignDashboard";
import CampaignForm from "./components/campaigns/CampaignForm";
import UserManagement from "./components/users/UserManagement";
import SystemSettings from "./components/settings/SystemSettings";
import TransactionHistory from "./components/transactions/TransactionHistory";
import WalletManagement from "./components/wallets/WalletManagement";
import FinancialOverview from "./components/financial/FinancialOverview";
import Login from "./components/auth/Login";
import ForgotPassword from "./components/auth/ForgotPassword";
import ProtectedRoute from "./Routes/ProtectedRoute";
import GuestRoute from "./Routes/GuestRoute";
import { LOCALSTORAGES } from "./constants/constant";
import ReferralsManagement from "./components/referrals/ReferralsManagement";
import UserReferrals from "./components/userReferrals/UserReferrals";
import KolManagement from "./components/kolMangement/KolManagement";
import PrivateCampaignManagement from "./components/campaigns/PrivateCampaignManagement";
import CodeManagement from "./components/codes/CodeManagement";

// Types
export interface User {
  id: string;
  email: string;
  role: "super_admin" | "campaign_manager";
  name: string;
}

export interface Campaign {
  id: string;
  name: string;
  description: string;
  status: string | "upcoming" | "active" | "ended" | "claimable" | "archived";
  startDate: string;
  endDate: string;
  tokenName: string;
  tokenSymbol: string;
  contractAddress: string;
  totalPool: number;
  participants: number;
  claimModel: "post_campaign" | "instant";
  claimStartDate?: string;
  claimEndDate?: string;
  claimedFrom: string;
  tasks: Task[];
}

export interface Task {
  id: string;
  type: "twitter_follow" | "twitter_retweet" | "twitter_like" | "telegram_join";
  instruction: string;
  reward: number;
  status: "active" | "inactive";
}

export interface PlatformUser {
  id: string;
  walletAddress: string;
  joinedDate: string;
  linkedAccounts: {
    twitter?: string;
    telegram?: string;
  };
  campaignsJoined: number;
  status: "active" | "flagged" | "banned";
  totalRewards: number;
}

export interface Transaction {
  id: string;
  type:
    | "claim"
    | "distribution"
    | "refund"
    | "transfer"
    | "deposit"
    | "withdrawal";
  userWalletAddress: string;
  campaignId?: string;
  campaignName?: string;
  tokenAmount: number;
  tokenSymbol: string;
  txHash: string;
  status: "pending" | "completed" | "failed" | "cancelled";
  timestamp: string;
  gasUsed?: number;
  gasFee?: number;
  blockNumber?: number;
  fromAddress: string;
  toAddress: string;
  notes?: string;
}

export interface AdminWallet {
  id: string;
  name: string;
  address: string;
  isActive: boolean;
  balance: number;
  tokenSymbol: string;
  lastUsed: string;
  createdDate: string;
  totalDistributed: number;
  totalReceived: number;
  transactionCount: number;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing authentication
    const token = localStorage.getItem("admin_token");
    const user = localStorage.getItem("admin_user");

    if (token && user) {
      setCurrentUser(JSON.parse(user));
      setIsAuthenticated(true);
    }

    setIsLoading(false);
  }, []);

  // const handleLogin = (
  //   email: string,
  //   password: string,
  //   twoFactorCode?: string
  // ) => {
  //   // Mock authentication logic
  //   if (email === "admin@airdrop.com" && password === "admin123") {
  //     const user: User = {
  //       id: "1",
  //       email,
  //       role: "super_admin",
  //       name: "Admin User",
  //     };

  //     localStorage.setItem("admin_token", "mock_token_123");
  //     localStorage.setItem("admin_user", JSON.stringify(user));

  //     setCurrentUser(user);
  //     setIsAuthenticated(true);
  //     toast.success("Login successful!");
  //     return true;
  //   }

  //   toast.error("Invalid credentials");
  //   return false;
  // };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Router>
      <div className="min-h-screen bg-background text-foreground">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route path="/" element={<GuestRoute />}>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>

          <Route path="/dashboard" element={<ProtectedRoute />}>
            <Route path="" element={<DashboardLayout user={currentUser} />}>
              <Route index element={<MainDashboard />} />
              <Route path="campaigns" element={<CampaignManagement />} />
              <Route
                path="private-campaigns/:id"
                element={<PrivateCampaignManagement />}
              />
              <Route
                path="create-codes/:id/:userId"
                element={<CodeManagement />}
              />

              <Route path="campaigns/new" element={<CampaignForm />} />
              <Route path="campaigns/:id/edit" element={<CampaignForm />} />
              <Route path="campaigns/:id" element={<CampaignDashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="kolMangement" element={<KolManagement />} />

              <Route path="transactions" element={<TransactionHistory />} />
              <Route path="wallets" element={<WalletManagement />} />
              <Route path="financial" element={<FinancialOverview />} />
              <Route path="referrals-code" element={<ReferralsManagement />} />
              <Route path="referrals-code-users" element={<UserReferrals />} />

              <Route path="settings" element={<SystemSettings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>

          {/* <Route
            path="/*"
            element={
              <DashboardLayout user={currentUser} onLogout={handleLogout}>
                <Routes>
                  <Route path="/" element={<MainDashboard />} />
                  <Route path="/campaigns" element={<CampaignManagement />} />
                  <Route path="/campaigns/new" element={<CampaignForm />} />
                  <Route
                    path="/campaigns/:id/edit"
                    element={<CampaignForm />}
                  />
                  <Route
                    path="/campaigns/:id"
                    element={<CampaignDashboard />}
                  />
                  <Route path="/users" element={<UserManagement />} />
                  <Route
                    path="/transactions"
                    element={<TransactionHistory />}
                  />
                  <Route path="/wallets" element={<WalletManagement />} />
                  <Route path="/financial" element={<FinancialOverview />} />
                  <Route path="/settings" element={<SystemSettings />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </DashboardLayout>
            }
          /> */}
        </Routes>
        <Toaster position="top-right" />
      </div>
    </Router>
  );
}

export default App;
